import React, {useState} from 'react';

import {Back, Play, Pause, Forward, Volume, Repeat, Shuffle} from './icons';
import Skeleton, {SkeletonTheme} from 'react-loading-skeleton';
import {ProgressBar} from './components';
import styles from './Footer.module.scss';
import ValueSlider from '../ValueSlider';
import classNames from 'classnames';
import {useCurrentSongContext} from '../../context/CurrentSongProvider';
import {useObsidianContext} from '../../context/ObsidianProvider';

// TODO: Will be moved to CurrentSong
export enum PlaybackMode {
  None = 'none',
  Shuffle = 'shuffle',
  Repeat = 'repeat',
}

const MAX_VOLUME = 100;

// TODO: Move this to a context for use app-wide, then we can put <SkeletonTheme> in <AppContext>
const isElectron = navigator.userAgent.toLowerCase().indexOf(' electron/') > 1;

export default function Footer() {
  const [mode, setMode] = useState(PlaybackMode.None);
  const {
    obsidian,
    connected,
    error: obsidianError,
    handlePlay,
    handlePause,
    handleForward,
    handleBackward,
  } = useObsidianContext();
  const {
    timer: {elapsed},
    musicLoading,
    playing,
    songMetaData,
    volume,
    setVolume,
  } = useCurrentSongContext();

  const renderControls = obsidian != null && !obsidianError;

  const handleVolumeUpdate = (value: number) => {
    obsidian.setVolume(value / 100);
    setVolume(value);
  };

  const shuffleClassName = classNames(
    styles.Shuffle,
    mode === PlaybackMode.Shuffle && styles.active,
  );

  const repeatClassName = classNames(
    styles.Repeat,
    mode === PlaybackMode.Repeat && styles.active,
  );

  const albumArtClassName = classNames(
    styles.AlbumArt,
    musicLoading && styles.musicLoading,
  );

  const songDetailsMarkup = renderControls && (
    <div className={styles.SongDetails}>
      {(songMetaData || musicLoading) && (
        <>
          {musicLoading ? (
            <Skeleton height={58} width={58} />
          ) : (
            <div className={albumArtClassName}>
              <img
                alt="cover"
                src={songMetaData ? songMetaData!.albumImage : ''}
              />
              <div className={styles.ImageOverlay} />
            </div>
          )}
        </>
      )}
      <div className={styles.ArtistWrapper}>
        <div className={styles.SongTitle}>
          {musicLoading ? <Skeleton /> : songMetaData ? songMetaData.name : ''}
        </div>
        <div className={styles.SongArtist}>
          {musicLoading ? (
            <Skeleton height={8} style={{marginTop: '4px'}} />
          ) : songMetaData ? (
            songMetaData.artist
          ) : (
            ''
          )}
        </div>
      </div>
    </div>
  );

  const playackControlsClassName = classNames(
    styles.PlaybackControls,
    musicLoading && styles.musicLoading,
  );

  const mainControlsMarkup =
    renderControls && connected ? (
      <div className={styles.MainControls}>
        <div className={playackControlsClassName}>
          <button
            className={styles.Back}
            onClick={handleBackward}
            type="button"
          >
            {Back}
          </button>
          <button
            className={styles.Play}
            onClick={playing ? handlePause : handlePlay}
            type="button"
          >
            {playing ? Pause : Play}
          </button>
          <button
            className={styles.Forward}
            onClick={handleForward}
            type="button"
          >
            {Forward}
          </button>
        </div>
        <div className={styles.ProgressBarWrapper}>
          <ProgressBar
            length={songMetaData ? songMetaData.duration : undefined}
            position={elapsed}
          />
        </div>
      </div>
    ) : null;

  const optionsMarkup = renderControls && (
    <div className={styles.Options}>
      <div className={styles.OptionButtons}>
        <button
          type="button"
          onClick={() =>
            setMode(
              mode === PlaybackMode.Shuffle
                ? PlaybackMode.None
                : PlaybackMode.Shuffle,
            )
          }
          className={shuffleClassName}
        >
          {Shuffle}
        </button>
        <button
          type="button"
          onClick={() =>
            setMode(
              mode === PlaybackMode.Repeat
                ? PlaybackMode.None
                : PlaybackMode.Repeat,
            )
          }
          className={repeatClassName}
        >
          {Repeat}
        </button>
      </div>
      <div className={styles.Volume}>
        <div className={styles.VolumeIcon}>
          <Volume volume={volume} />
        </div>
        <ValueSlider
          progress={volume}
          max={MAX_VOLUME}
          primaryColor="#646464"
          onUpdate={handleVolumeUpdate}
        />
      </div>
    </div>
  );

  const footerClassName = classNames(
    styles.Footer,
    !isElectron && styles.noElectron,
  );

  return (
    <SkeletonTheme
      color={isElectron ? undefined : undefined}
      highlightColor={isElectron ? undefined : undefined} // TODO: Find better skeleton colors for electron
    >
      <div className={footerClassName}>
        {songDetailsMarkup}
        {mainControlsMarkup}
        {optionsMarkup}
      </div>
    </SkeletonTheme>
  );
}
